import type { Inherit, MixinMembers } from '../../types/leaflet-class';
import type {
  LeafletClass,
  LeafletClassFactory,
} from '../../types/leaflet-class';
import type MarkerLimits from '../Mixins/MarkerLimits';
import type { IEditMarker } from './L.PM.Edit.Marker';
import type { IEditCircleMarker } from './L.PM.Edit.CircleMarker';
import type { IEditCircle } from './L.PM.Edit.Circle';
import type { IEditImageOverlay } from './L.PM.Edit.ImageOverlay';
import type { IEditLayerGroup } from './L.PM.Edit.LayerGroup';
import type { IEditLine } from './L.PM.Edit.Line';
import type { IEditPolygon } from './L.PM.Edit.Polygon';
import type { IEditRectangle } from './L.PM.Edit.Rectangle';
import type { IEditText } from './L.PM.Edit.Text';

import type {
  EditOptions,
  VertexValidationArgs,
  VertexValidationFn,
} from '../../types/options';
export type { EditOptions } from '../../types/options';

/**
 * Extended marker with validation chain
 */
interface ExtendedMarker extends L.Marker {
  _cancelDragEventChain?: L.LatLng | null;
  _latlng: L.LatLng;
  update(): this;
}

/**
 * Extended layer with PM and map - using type intersection to avoid protected property issues
 */
type PMLayer = L.Layer & {
  _map?: ExtendedMap;
  options: L.LayerOptions & {
    pane?: string;
  };
};

/**
 * Extended map with PM
 */
type ExtendedMap = L.Map & {
  pm: {
    globalOptions: {
      panes?: {
        layerPane?: string;
        vertexPane?: string;
        markerPane?: string;
      };
    };
    removeLayer: (e: { target: L.Layer }) => void;
  };
};

/**
 * Edit class interface
 */
export interface IEdit {
  options: EditOptions;
  _map?: ExtendedMap;
  _layer: L.Layer & { _map?: L.Map };
  _shape?: string;

  setOptions(options: EditOptions): void;
  getOptions(): EditOptions;
  applyOptions(): void;
  isPolygon(): boolean;
  getShape(): string | undefined;
  _setPane(
    layer: PMLayer,
    type: 'layerPane' | 'vertexPane' | 'markerPane'
  ): void;
  remove(): void;
  _vertexValidation(
    type: 'move' | 'add' | 'remove',
    e: L.LeafletEvent & { target: ExtendedMarker }
  ): boolean;
  _vertexValidationDrag(marker: ExtendedMarker): boolean;
  _vertexValidationDragEnd(marker: ExtendedMarker): boolean;
}
import SnapMixin from '../Mixins/Snapping';
import DragMixin from '../Mixins/Dragging';
import RotateMixin from '../Mixins/Rotating';
import EventMixin from '../Mixins/Events';

const Edit = (L.Class as unknown as LeafletClassFactory).extend<
  IEdit,
  [L.Layer],
  [typeof DragMixin, typeof SnapMixin, typeof RotateMixin, typeof EventMixin]
>({
  includes: [DragMixin, SnapMixin, RotateMixin, EventMixin],
  options: {
    snappable: true, // TODO: next major Release, rename it to allowSnapping
    snapDistance: 20,
    allowSelfIntersection: true,
    allowSelfIntersectionEdit: false,
    preventMarkerRemoval: false,
    removeLayerBelowMinVertexCount: true,
    limitMarkersToCount: -1,
    hideMiddleMarkers: false,
    snapSegment: true,
    syncLayersOnDrag: false,
    draggable: true, // TODO: next major Release, rename it to allowDragging
    allowEditing: true, // disable all interactions on a layer which are activated with `enable()`. For example a Circle can't be dragged in Edit-Mode
    allowRemoval: true,
    allowCutting: true,
    allowRotation: true,
    addVertexOn: 'click',
    removeVertexOn: 'contextmenu',
    removeVertexValidation: undefined,
    addVertexValidation: undefined,
    moveVertexValidation: undefined,
    resizeableCircleMarker: false,
    resizeableCircle: true,
    snapMiddle: false,
    snapVertex: true,
  },
  setOptions(this: IEdit, options: EditOptions) {
    L.Util.setOptions(this, options);
  },
  getOptions(this: IEdit) {
    return this.options;
  },
  applyOptions(this: IEdit) {},
  isPolygon(this: IEdit) {
    // if it's a polygon, it means the coordinates array is multi dimensional
    return this._layer instanceof L.Polygon;
  },
  getShape(this: IEdit) {
    return this._shape;
  },
  _setPane(
    this: IEdit,
    layer: PMLayer,
    type: 'layerPane' | 'vertexPane' | 'markerPane'
  ) {
    if (type === 'layerPane') {
      layer.options.pane =
        (this._map!.pm.globalOptions.panes &&
          this._map!.pm.globalOptions.panes.layerPane) ||
        'overlayPane';
    } else if (type === 'vertexPane') {
      layer.options.pane =
        (this._map!.pm.globalOptions.panes &&
          this._map!.pm.globalOptions.panes.vertexPane) ||
        'markerPane';
    } else if (type === 'markerPane') {
      layer.options.pane =
        (this._map!.pm.globalOptions.panes &&
          this._map!.pm.globalOptions.panes.markerPane) ||
        'markerPane';
    }
  },
  remove(this: IEdit) {
    const map = this._map || this._layer._map;
    map!.pm.removeLayer({ target: this._layer });
  },
  _vertexValidation(
    this: IEdit,
    type: 'move' | 'add' | 'remove',
    e: L.LeafletEvent & { target: ExtendedMarker }
  ) {
    const marker = e.target;
    const args: VertexValidationArgs = { layer: this._layer, marker, event: e };

    let validationFnc: keyof EditOptions = '';
    if (type === 'move') {
      validationFnc = 'moveVertexValidation';
    } else if (type === 'add') {
      validationFnc = 'addVertexValidation';
    } else if (type === 'remove') {
      validationFnc = 'removeVertexValidation';
    }

    // if validation goes wrong, we return false
    if (
      this.options[validationFnc] &&
      typeof this.options[validationFnc] === 'function' &&
      !(this.options[validationFnc] as VertexValidationFn)(args)
    ) {
      if (type === 'move') {
        marker._cancelDragEventChain = marker.getLatLng();
      }
      return false;
    }

    marker._cancelDragEventChain = null;
    return true;
  },
  _vertexValidationDrag(this: IEdit, marker: ExtendedMarker) {
    // we reset the marker to the place before it was dragged. We need this, because we can't stop the drag process in a `dragstart` | `movestart` listener
    if (marker._cancelDragEventChain) {
      marker._latlng = marker._cancelDragEventChain;
      marker.update();
      return false;
    }
    return true;
  },
  _vertexValidationDragEnd(this: IEdit, marker: ExtendedMarker) {
    if (marker._cancelDragEventChain) {
      marker._cancelDragEventChain = null;
      return false;
    }
    return true;
  },
}) as EditClass;

export type EditBase = Inherit<
  MixinMembers<
    [typeof DragMixin, typeof SnapMixin, typeof RotateMixin, typeof EventMixin]
  >,
  IEdit
>;

export interface EditClass extends LeafletClass<EditBase, [L.Layer]> {
  Marker: LeafletClass<EditInstances['Marker'], [L.Marker]>;
  CircleMarker: LeafletClass<EditInstances['CircleMarker'], [L.CircleMarker]>;
  Circle: LeafletClass<EditInstances['Circle'], [L.Circle]>;
  ImageOverlay: LeafletClass<EditInstances['ImageOverlay'], [L.ImageOverlay]>;
  LayerGroup: LeafletClass<EditInstances['LayerGroup'], [L.LayerGroup]>;
  Line: LeafletClass<
    EditInstances['Line'],
    [
      L.Polyline<
        | GeoJSON.LineString
        | GeoJSON.MultiLineString
        | GeoJSON.Polygon
        | GeoJSON.MultiPolygon
      >,
    ]
  >;
  Polygon: LeafletClass<EditInstances['Polygon'], [L.Polygon]>;
  Rectangle: LeafletClass<EditInstances['Rectangle'], [L.Rectangle]>;
  Text: LeafletClass<EditInstances['Text'], [L.Marker]>;
}

export default Edit;

export interface EditInstances {
  Marker: Inherit<EditBase, IEditMarker>;
  CircleMarker: Inherit<EditBase, IEditCircleMarker>;
  Circle: Inherit<EditInstances['CircleMarker'], IEditCircle>;
  ImageOverlay: Inherit<EditBase, IEditImageOverlay>;
  LayerGroup: Inherit<{}, IEditLayerGroup>;
  Line: Inherit<
    Inherit<EditBase, MixinMembers<[typeof MarkerLimits]>>,
    IEditLine
  >;
  Polygon: Inherit<EditInstances['Line'], IEditPolygon>;
  Rectangle: Inherit<EditInstances['Polygon'], IEditRectangle>;
  Text: Inherit<EditBase, IEditText>;
}
